import React, { useState, useRef, memo } from 'react';
import { Download, Table as TableIcon, Code, LineChart as ChartIcon, FileText, FileDown, Loader2, Maximize2, X, AlertCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { toPng } from 'html-to-image';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, prism } from 'react-syntax-highlighter/dist/esm/styles/prism';

const PlotContainer = memo(({ children, data, sql, title, rowCount, isMapResult = false, tableOnly = false, spec, isLoading = false }) => {
  const showPlotTab = !isMapResult && !tableOnly;
  const showSqlTab = Boolean(sql) || isMapResult;
  // Use isMapResult directly in initialization to avoid flash
  const [activeTab, setActiveTab] = useState(() => {
    if (isMapResult) return 'sql';
    if (tableOnly) return 'data';
    return 'plot';
  });
  const [isMaximized, setIsMaximized] = useState(false);
  const [exportError, setExportError] = useState(null);
  const plotRef = useRef(null);
  const maximizedPlotRef = useRef(null);
  const isDark = document.documentElement.classList.contains('dark');

  // Switch to SQL tab if it becomes a map result later
  React.useEffect(() => {
    if (isMapResult && activeTab !== 'sql') {
      setActiveTab('sql');
    }
  }, [isMapResult, activeTab]);

  // Table-only results: never keep the (hidden) plot tab selected
  React.useEffect(() => {
    if (tableOnly && activeTab === 'plot') {
      setActiveTab('data');
    }
  }, [tableOnly, activeTab]);

  // Calculate row count from data if not provided
  const displayRowCount = rowCount !== undefined ? rowCount : (data ? data.length : 0);

  /** Plot tab placeholder: SQL often arrives before rows/spec; avoid flashing "no viz" while the stream is active. */
  const expectingVisualization =
    showPlotTab &&
    (isLoading ||
      (data && data.length > 0) ||
      (rowCount !== undefined && rowCount > 0));

  const exportCSV = () => {
    if (!data || !data.length) return;
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => JSON.stringify(row[header])).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${title || 'chart_data'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportSQL = () => {
    if (!sql) return;
    const blob = new Blob([sql], { type: 'text/plain;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${title || 'query'}.sql`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportPlot = async () => {
    // Use maximized plot ref if available, otherwise use regular plot ref
    const refToUse = isMaximized && maximizedPlotRef.current ? maximizedPlotRef.current : plotRef.current;

    if (refToUse === null) {
      return;
    }

    setExportError(null);
    try {
      const exportBackground = document.documentElement.classList.contains('dark') ? '#030712' : '#ffffff';
      const dataUrl = await toPng(refToUse, {
        backgroundColor: exportBackground,
        cacheBust: true,
        pixelRatio: 3,
        style: {
          padding: '32px',
          background: exportBackground,
        }
      });
      const link = document.createElement('a');
      link.download = `${title || 'plot'}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Failed to export plot:', err);
      setExportError('Failed to export plot. Please try again.');
    }
  };

  return (
    <div className="mt-4 flex flex-col bg-white dark:bg-gray-950 border border-gray-100 dark:border-gray-800 rounded-2xl shadow-sm overflow-hidden">
      {/* Container Header/Tabs */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50">
        <div className="flex items-center gap-1">
          {!isMapResult && (
            <>
              {showPlotTab && (
                <button
                  onClick={() => setActiveTab('plot')}
                  className={cn(
                    "flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all",
                    activeTab === 'plot'
                      ? "bg-white dark:bg-gray-800 text-emerald-600 dark:text-emerald-400 shadow-sm"
                      : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  )}
                >
                  <ChartIcon size={12} />
                  Plot
                </button>
              )}
              <button
                onClick={() => setActiveTab('data')}
                className={cn(
                  "flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all",
                  activeTab === 'data'
                    ? "bg-white dark:bg-gray-800 text-emerald-600 dark:text-emerald-400 shadow-sm"
                    : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                )}
              >
                <TableIcon size={12} />
                Data
              </button>
            </>
          )}
          {showSqlTab && (
            <button
              onClick={() => setActiveTab('sql')}
              className={cn(
                "flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all",
                activeTab === 'sql'
                  ? "bg-white dark:bg-gray-800 text-emerald-600 dark:text-emerald-400 shadow-sm"
                  : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              )}
            >
              <Code size={12} />
              Code
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          {!isMapResult && (displayRowCount !== undefined || sql) && (
            <span className="px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-800/50 text-xs font-medium text-gray-600 dark:text-gray-400">
              {displayRowCount.toLocaleString()} {displayRowCount === 1 ? 'row found' : 'rows found'}
            </span>
          )}
          <div className="flex items-center gap-2">
            {showPlotTab && activeTab === 'plot' && (
              <button
                onClick={exportPlot}
                className="p-1.5 text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-white dark:hover:bg-gray-800 rounded-lg transition-all"
                title="Export Plot as PNG"
              >
                <Download size={14} />
              </button>
            )}
            {activeTab === 'data' && (
              <button
                onClick={exportCSV}
                className="p-1.5 text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-white dark:hover:bg-gray-800 rounded-lg transition-all"
                title="Export Data as CSV"
              >
                <FileDown size={14} />
              </button>
            )}
            {activeTab === 'sql' && (
              <button
                onClick={exportSQL}
                className="p-1.5 text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-white dark:hover:bg-gray-800 rounded-lg transition-all"
                title="Export SQL"
              >
                <FileText size={14} />
              </button>
            )}
            <button
              onClick={() => setIsMaximized(true)}
              className="p-1.5 text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-white dark:hover:bg-gray-800 rounded-lg transition-all"
              title="Maximize"
            >
              <Maximize2 size={14} />
            </button>
          </div>
        </div>
      </div>

      {exportError && showPlotTab && activeTab === 'plot' && (
        <div className="px-3 py-2 mx-2 mb-1 flex items-center gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40">
          <AlertCircle size={14} className="text-red-500 shrink-0" />
          <p className="text-[10px] font-bold text-red-600 dark:text-red-300 flex-1">{exportError}</p>
          <button
            type="button"
            onClick={() => setExportError(null)}
            className="p-0.5 hover:bg-red-100 dark:hover:bg-red-800/50 rounded"
            aria-label="Dismiss"
          >
            <X size={12} className="text-red-400" />
          </button>
        </div>
      )}

      {/* Content Area */}
      <div className="flex-1 overflow-hidden p-2">
        {activeTab === 'plot' && showPlotTab && (
          <div className="min-h-[320px] w-full flex flex-col items-center justify-center bg-white dark:bg-gray-950 relative" ref={plotRef}>
            {title && (
              <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100 tracking-tight mb-1 px-3 text-center w-full leading-snug">
                {title}
              </h4>
            )}
            <div className="w-full h-[280px] relative">
              {children || (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  {expectingVisualization ? (
                    <>
                      <Loader2 size={24} className="mb-2 animate-spin text-emerald-500" />
                      <p className="text-[10px] font-bold uppercase tracking-widest">Loading visualization...</p>
                    </>
                  ) : (
                    <>
                      <ChartIcon size={24} className="mb-2 opacity-20" />
                      <p className="text-[10px] font-bold uppercase tracking-widest">No visualization available</p>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'data' && (
          <div className="h-64 w-full overflow-auto">
            {data && data.length > 0 ? (
              <table className="w-full text-left border-collapse">
                <thead className="sticky top-0 bg-white dark:bg-gray-950 z-10">
                  <tr className="border-b border-gray-100 dark:border-gray-800">
                    {Object.keys(data[0] || {}).map(key => (
                      <th key={key} className="py-2 px-3 text-[9px] font-black text-gray-400 uppercase tracking-widest bg-gray-50/50 dark:bg-gray-900/50">
                        {key}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-900/50">
                  {data.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50/30 dark:hover:bg-gray-900/30 transition-colors">
                      {Object.values(row).map((val, j) => (
                        <td key={j} className="py-2 px-3 text-xs font-bold text-gray-700 dark:text-gray-300">
                          {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400 text-xs">
                No data to display
              </div>
            )}
          </div>
        )}

        {activeTab === 'sql' && (
          <div className="h-64 w-full rounded-xl overflow-hidden font-mono text-[11px] leading-relaxed">
            {sql ? (
              <SyntaxHighlighter
                language="sql"
                style={isDark ? vscDarkPlus : prism}
                customStyle={{
                  margin: 0,
                  padding: '1.5rem',
                  fontSize: '11px',
                  lineHeight: '1.6',
                  height: '100%',
                  background: 'transparent',
                }}
                codeTagProps={{
                  style: {
                    fontFamily: 'inherit',
                  }
                }}
              >
                {sql}
              </SyntaxHighlighter>
            ) : (
              <div className="p-4 text-gray-400">-- No SQL available for this visualization</div>
            )}
          </div>
        )}
      </div>

      {/* Maximized Plot Modal */}
      {isMaximized && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm transition-opacity"
            onClick={() => setIsMaximized(false)}
          />
          <div className="relative bg-white dark:bg-gray-950 w-full h-full max-w-[95vw] max-h-[95vh] rounded-[40px] shadow-2xl border border-gray-100 dark:border-gray-800 overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50">
              <div className="flex items-center gap-3">
                {title && (
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white tracking-tight">
                    {title}
                  </h3>
                )}
                {!isMapResult && (displayRowCount !== undefined || sql) && (
                  <span className="px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-800/50 text-xs font-medium text-gray-600 dark:text-gray-400">
                    {displayRowCount.toLocaleString()} {displayRowCount === 1 ? 'row found' : 'rows found'}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {showPlotTab && activeTab === 'plot' && (
                  <button
                    onClick={exportPlot}
                    className="p-2 text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-white dark:hover:bg-gray-800 rounded-lg transition-all"
                    title="Export Plot as PNG"
                  >
                    <Download size={16} />
                  </button>
                )}
                <button
                  onClick={() => setIsMaximized(false)}
                  className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors rounded-xl hover:bg-gray-50 dark:hover:bg-gray-900"
                  title="Close"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-hidden p-6 min-h-0">
              {activeTab === 'plot' && showPlotTab && (
                <div className="w-full h-full flex flex-col bg-white dark:bg-gray-950 min-h-0" ref={maximizedPlotRef}>
                  <div className="w-full flex-1 min-h-0">
                    {children || (
                      <div className="flex flex-col items-center justify-center h-full text-gray-400">
                        {expectingVisualization ? (
                          <>
                            <Loader2 size={24} className="mb-2 animate-spin text-emerald-500" />
                            <p className="text-[10px] font-bold uppercase tracking-widest">Loading visualization...</p>
                          </>
                        ) : (
                          <>
                            <ChartIcon size={24} className="mb-2 opacity-20" />
                            <p className="text-[10px] font-bold uppercase tracking-widest">No visualization available</p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'data' && (
                <div className="h-full w-full overflow-auto">
                  {data && data.length > 0 ? (
                    <table className="w-full text-left border-collapse">
                      <thead className="sticky top-0 bg-white dark:bg-gray-950 z-10">
                        <tr className="border-b border-gray-100 dark:border-gray-800">
                          {Object.keys(data[0] || {}).map(key => (
                            <th key={key} className="py-2 px-3 text-[9px] font-black text-gray-400 uppercase tracking-widest bg-gray-50/50 dark:bg-gray-900/50">
                              {key}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 dark:divide-gray-900/50">
                        {data.map((row, i) => (
                          <tr key={i} className="hover:bg-gray-50/30 dark:hover:bg-gray-900/30 transition-colors">
                            {Object.values(row).map((val, j) => (
                              <td key={j} className="py-2 px-3 text-xs font-bold text-gray-700 dark:text-gray-300">
                                {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400 text-xs">
                      No data to display
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'sql' && (
                <div className="h-full w-full overflow-auto">
                <div className="h-full w-full rounded-xl overflow-hidden font-mono text-[11px] leading-relaxed">
                  {sql ? (
                    <SyntaxHighlighter
                      language="sql"
                      style={isDark ? vscDarkPlus : prism}
                      customStyle={{
                        margin: 0,
                        padding: '1.5rem',
                        fontSize: '11px',
                        lineHeight: '1.6',
                        height: '100%',
                        background: 'transparent',
                      }}
                      codeTagProps={{
                        style: {
                          fontFamily: 'inherit',
                        }
                      }}
                    >
                      {sql}
                    </SyntaxHighlighter>
                  ) : (
                    <div className="p-4 text-gray-400">-- No SQL available for this visualization</div>
                  )}
                </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default PlotContainer;
